import React, { useState, useRef } from 'react';
import { useForm, SubmitHandler, FormProvider, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/useToast';
import { Collaborator, createContextualCollaboratorSchema } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { normalizeCollaboratorFormData } from '@/utils/form-validation/formDataNormalizer';
import { CollaboratorModal } from '@/features/Collaborators/Shared/CollaboratorModal';

interface CollaboratorFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    context: CollaboratorFormContext;
    collaboratorId: string | null;
    onSubmit: (data: Collaborator) => Promise<void>;
    title: string;
    children?: React.ReactNode;
    maxWidth?: string;
    currentFieldsets?: string[]; // Array de fieldsets ativos para validação contextual
}

const CollaboratorFormModal: React.FC<CollaboratorFormModalProps> = ({
    isOpen,
    onClose,
    context,
    collaboratorId,
    onSubmit,
    title,
    children,
    maxWidth,
    currentFieldsets,
}) => {
    const [formSubmitter, setFormSubmitter] = useState<(() => void) | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    // Actions que serão passadas para o CollaboratorModal
    const actions = (
        <div className="flex justify-between w-full">
            <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSubmitting}
            >
                Cancelar
            </button>
            <button
                type="button"
                onClick={() => formSubmitter?.()}
                className="btn-primary"
                disabled={isSubmitting || !formSubmitter}
            >
                {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </button>
        </div>
    );

    return (
        <CollaboratorModal
            isOpen={isOpen}
            onClose={onClose}
            collaboratorId={collaboratorId}
            title={title}
            actions={actions}
            maxWidth={maxWidth}
        >
            {(collaborator: Collaborator) => (
                <CollaboratorFormContent
                    collaborator={collaborator}
                    context={context}
                    collaboratorId={collaboratorId!}
                    onSubmit={onSubmit}
                    onClose={onClose}
                    setFormSubmitter={setFormSubmitter}
                    setIsSubmitting={setIsSubmitting}
                    children={children}
                    currentFieldsets={currentFieldsets}
                />
            )}
        </CollaboratorModal>
    );
};

interface CollaboratorFormContentProps {
    collaborator: Collaborator;
    context: CollaboratorFormContext;
    collaboratorId: string;
    onSubmit: (data: Collaborator) => Promise<void>;
    onClose: () => void;
    setFormSubmitter: (submitter: (() => void) | null) => void;
    setIsSubmitting: (isSubmitting: boolean) => void;
    children?: React.ReactNode;
    currentFieldsets?: string[];
}

const CollaboratorFormContent: React.FC<CollaboratorFormContentProps> = ({
    collaborator,
    context,
    collaboratorId,
    onSubmit,
    onClose,
    setFormSubmitter,
    setIsSubmitting,
    children,
    currentFieldsets,
}) => {
    const { error, warning } = useToast();

    // Refs para evitar dependências que causam loop infinito
    const toastRef = useRef({ error, warning });
    toastRef.current = { error, warning };

    // 🔄 NORMALIZAR dados iniciais para evitar controlled vs uncontrolled
    const normalizedDefaultValues = React.useMemo(() => {
        return normalizeCollaboratorFormData(collaborator);
    }, [collaborator]);

    // 🚀 FORM SETUP com dados normalizados e schema contextual
    const contextualSchema = React.useMemo(() => {
        return createContextualCollaboratorSchema(context, currentFieldsets);
    }, [context, currentFieldsets]);

    const form = useForm<Collaborator>({
        resolver: zodResolver(contextualSchema),
        defaultValues: normalizedDefaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    const {
        handleSubmit,
        formState: { errors, isSubmitting }
    } = form;

    // Sincronizar isSubmitting com o estado externo
    React.useEffect(() => {
        setIsSubmitting(isSubmitting);
    }, [isSubmitting]);

    // 📝 SUBMIT HANDLER
    const onFormSubmit = React.useCallback<SubmitHandler<Collaborator>>(async (data) => {
        try {
            await onSubmit(data);
            onClose(); // Fechar modal após sucesso
        } catch (submitError) {
            const errorMessage = submitError instanceof Error ? submitError.message : 'Erro desconhecido';
            toastRef.current.error('Erro ao salvar dados', errorMessage);
            throw submitError;
        }
    }, [onSubmit, onClose]);

    // Callback de erro após validação (igual ao ProfileUpdateWizard)
    const onInvalid = React.useCallback((invalidErrors: FieldErrors<Collaborator>) => {
        console.group(`❌ onInvalid – erros de validação [${context}]`);
        console.log('Fieldsets ativos:', currentFieldsets);
        console.log('Erros encontrados:');
        Object.entries(invalidErrors).forEach(([field, error]) => {
            console.log(`  - ${field}:`, error?.message || error);
        });
        console.groupEnd();

        const errorCount = Object.keys(invalidErrors).length;
        toastRef.current.warning(`Você tem ${errorCount} campo(s) obrigatório(s) não preenchido(s).`);
    }, [context, currentFieldsets]);

    // Handler que será passado para o componente filho (igual ao ProfileUpdateWizard)
    const handleFormSubmit = React.useCallback(async () => {
        handleSubmit(onFormSubmit, onInvalid)();
    }, [handleSubmit, onFormSubmit, onInvalid]);

    // Expor função de submit para o botão externo
    React.useEffect(() => {
        setFormSubmitter(() => handleFormSubmit);
    }, [handleFormSubmit]);

    return (
        <FormProvider {...form}>
            <form onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }} noValidate className="h-full">
                <div className="p-6">
                    {/* 
                    FormProvider está pronto para receber children específicos do contexto 
                    (ex: DHOUpdateModal, TechUpdateModal, etc.)
                    */}
                    {children || (
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                            <p className="text-gray-600 text-sm text-center">
                                FormProvider configurado. Aguardando componentes filhos específicos do contexto.
                            </p>
                            <div className="mt-2 text-xs text-gray-500 text-center">
                                Contexto: <span className="font-mono bg-gray-200 px-1 rounded">{context}</span>
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default CollaboratorFormModal; 